import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { VehicleRunningTotalDTO } from 'src/app/base/models/models.swagger.generated';
import { RunningTotalsService } from './running-totals.service';
import { groupByModule, parseRunningTotal } from './running-totals-display.definitions';

@Component({
  selector: 'app-running-totals',
  templateUrl: './running-totals.component.html',
  styleUrls: ['./running-totals.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslatePipe],
  providers: [RunningTotalsService],
})
export class RunningTotalsComponent {

  private readonly runningTotalsService = inject(RunningTotalsService);

  /** Bound to the input field. Only applied to the request once the user triggers a load. */
  public readonly vehicleId = signal<number | null>(1);

  private readonly requestedVehicleId = signal<number | null>(1);

  private readonly runningTotals = rxResource({
    params: () => this.requestedVehicleId() ?? undefined,
    stream: ({ params: vehicleId }) => this.runningTotalsService.getRunningTotals(vehicleId),
    defaultValue: [] as VehicleRunningTotalDTO[],
  });

  public readonly isLoading = this.runningTotals.isLoading;

  public readonly groups = computed(() => {
    if (!this.runningTotals.hasValue()) {
      return [];
    }
    return groupByModule(this.runningTotals.value().map(parseRunningTotal).filter(stat => stat != null));
  });

  public onLoad(): void {
    this.requestedVehicleId.set(this.vehicleId());
    // Re-requests when the id did not change; a no-op while the id change is already loading.
    this.runningTotals.reload();
  }

  public formatNumber(value: number | null, decimals: number, unit: string): string {
    if (value == null) {
      return '—';
    }
    return `${value.toFixed(decimals)} ${unit}`;
  }

  public gaugePercent(value: number | null, referenceMax: number): number {
    if (value == null) {
      return 0;
    }
    return Math.min(100, Math.max(0, Math.round((value / referenceMax) * 100)));
  }

  public formatUpdated(updatedUtc: DateTime): string {
    return updatedUtc.toFormat('dd.MM.yyyy HH:mm');
  }

  public humanizeStatisticName(statisticName: string): string {
    return statisticName.replace(/([a-z])([A-Z])/g, '$1 $2');
  }
}
